// src/lib/kpiMath.js
// STATUS: active | UPDATED: 2026-09-23
// LOG:
//   2026-09-23  Split out of kpi.js so it can be tested.
//
// THE DATE AND GROWTH MATH, WITH NO DEPENDENCIES.
//
// Separate from kpi.js for one blunt reason: that file imports the Supabase
// client, which reads `import.meta.env` and cannot be loaded by node, so none
// of the arithmetic below could be tested while it lived there. A month
// boundary that is wrong by one timezone, or a growth figure that reads
// Infinity, is exactly the class of bug a test catches and a reading does not.
//
// Everything here is a pure function of its arguments. Keep it that way.
//
// The date helpers moved to localDate.js on 2026-09-23, when a third copy of
// the same local-parts rule was about to be written. Re-exported here so every
// existing call site is unchanged.
//
// EXPLICIT .js ON THE IMPORT. Vite resolves either form, node only resolves
// this one, and the tests load this file directly under node.

import { monthKey, shiftMonth } from './localDate.js'

export { monthKey, shiftMonth }

// Jazz names units first and on its own, ahead of the four gross numbers, so
// the screen does too. Ordering by slug would put finance first and lose that.
export const KPI_ORDER = ['units_sold', 'gross_sales', 'gross_finance', 'gross_service', 'gross_parts']

// His floor, in percent. The range he gave was 10 to 20 and a bar has to be one
// number: 10 is the one he would rather a store beat than miss, and a bar
// nobody clears stops being a bar.
export const GROWTH_FLOOR = 10
export const GROWTH_STRETCH = 20

// You report a month once it has ended, so the screen opens on the last
// complete one rather than the one somebody is still living in.
export function lastCompleteMonth(now = new Date()) {
  return monthKey(new Date(now.getFullYear(), now.getMonth() - 1, 1))
}

export function monthLabel(key) {
  const [y, m] = key.split('-').map(Number)
  return new Date(y, m - 1, 1).toLocaleDateString(undefined, { month: 'long', year: 'numeric' })
}

// Month over month, in percent, against his bar.
//
// Returns null rather than 0 when there is nothing to compare, because "no
// previous month" and "flat" are different facts, and a screen that shows them
// the same way is lying about the first month a store ever reports.
//
// A previous value of zero has no percentage change that means anything, so it
// is null too rather than Infinity.
export function growth(current, previous) {
  if (!Number.isFinite(current) || !Number.isFinite(previous) || previous === 0) return null
  return ((current - previous) / previous) * 100
}

export function growthBand(pct) {
  if (pct == null) return 'none'
  if (pct >= GROWTH_STRETCH) return 'stretch'
  if (pct >= GROWTH_FLOOR) return 'met'
  if (pct >= 0) return 'short'
  return 'down'
}

export function formatValue(unit, value) {
  if (!Number.isFinite(value)) return null
  if (unit === 'dollars') {
    return value.toLocaleString(undefined, { style: 'currency', currency: 'USD', maximumFractionDigits: 0 })
  }
  return value.toLocaleString()
}

// Shared by the screen and by saveMonth, so "1,250" and "$1,250" and " 1250 "
// all mean the same number in both places. A blank is null rather than 0: zero
// units in a month is a real and terrible figure somebody might genuinely
// report, and it must never be what "I do not have this yet" looks like.
export function parseValue(raw) {
  const text = String(raw == null ? '' : raw).trim()
  if (text === '') return null
  const n = Number(text.replace(/[$,\s]/g, ''))
  return Number.isFinite(n) ? n : NaN
}
